import React from 'react';
import { cn } from '@/lib/utils';

const Timeline = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} {...props} className={cn('space-y-0 w-full', className)} />
));

Timeline.displayName = 'Timeline';

const TimelineItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { active?: boolean }
>(({ className, active, ...props }, ref) => (
  <div ref={ref} {...props} className={cn('flex', className)} />
));

TimelineItem.displayName = 'TimelineItem';

const TimelineItemLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cn(
      'w-20 py-3 flex-shrink-0 text-sm text-muted-foreground',
      className
    )}
  />
));

TimelineItemLabel.displayName = 'TimelineItemLabel';

const TimelineConnector = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    active?: boolean;
    onClick?: () => void;
  }
>(({ className, active, onClick, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cn(
      'w-12 flex-shrink-0 flex items-center justify-center',
      className
    )}
  >
    <div className="w-px h-full bg-border relative">
      <button
        onClick={onClick}
        className={cn(
          'absolute top-3 left-1/2 w-3 h-3 rounded-full transform -translate-x-1/2 transition-colors duration-200',
          active ? 'bg-primary' : 'bg-border hover:bg-primary/50'
        )}
        aria-label={
          active ? 'Current timeline item' : 'Navigate to this timeline item'
        }
      />
    </div>
  </div>
));

TimelineConnector.displayName = 'TimelineConnector';

const TimelineContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cn('flex-grow py-3 border-b border-border', className)}
  />
));

TimelineContent.displayName = 'TimelineContent';

const TimelineContentLabel = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3 ref={ref} {...props} className={cn('font-semibold', className)} />
));

TimelineContentLabel.displayName = 'TimelineContentLabel';

const TimelineContentDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    {...props}
    className={cn('text-sm text-muted-foreground mt-1', className)}
  />
));

TimelineContentDescription.displayName = 'TimelineContentDescription';

export {
  Timeline,
  TimelineItem,
  TimelineItemLabel,
  TimelineConnector,
  TimelineContent,
  TimelineContentLabel,
  TimelineContentDescription,
};
